import { useState } from "react";
import { Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import SelectMUI from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import Checkbox from "./Checkbox";
import TextField from "@mui/material/TextField";
import ListItemText from "@mui/material/ListItemText";
import i18next from "i18next";
import {
  FIELD_REQUIRED,
  SELECT_SOME_OPTIONS,
  ANY,
  SELECT_A_OPTION,
  N_NUMBER_SELECTED,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { getReactHookFormNestedError } from "../../utils/errors";

const SelectMultiple = ({
  dataTour = "",
  control,
  rules = { required: { value: true, message: i18next.t(FIELD_REQUIRED) } },
  errors = {},
  label = "",
  variant = "filled",
  MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 48 * 4.5 + 8 * 2,
        width: 250,
      },
    },
  },
  name = "",
  value = [],
  size = "medium",
  fullWidth = false,
  idLabel = `select-${name}`,
  options = [],
  displayEmpty = false,
  actionsChange = () => {},
  disabled = false,
  placeholder = "",
  showEmptyOption = false,
  sx = {},
  className = "",
  doTranslate = false,
  isRounded = false,
  showAddAction = false,
  labelAddAction = "",
  addAction = () => {},
  errorAddAction = null,
  showAllOption = false,
  backgroundColor = "",
  helperText = "",
  maxSelections,
}) => {
  const hiddenLabel = variant === "filled" && !label;
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const [newOption, setNewOption] = useState("");

  // Helper to get all option values
  const allOptionValues = options.map((option) => option.value);

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      defaultValue={value}
      render={({ field }) => (
        <FormControl
          className={className}
          fullWidth={fullWidth}
          variant={variant}
          error={!!error}
          disabled={disabled}
          sx={{ ...sx }}
          data-tour={dataTour}
          hiddenLabel={hiddenLabel}
        >
          {label && <InputLabel id={idLabel}>{label}</InputLabel>}
          <SelectMUI
            size={size}
            placeholder={placeholder}
            labelId={idLabel}
            label={label}
            variant={variant}
            displayEmpty={displayEmpty}
            multiple
            renderValue={(selected) => {
              if (!selected || selected.length === 0)
                return t(SELECT_SOME_OPTIONS);
              if (selected.length > 4) {
                return t(N_NUMBER_SELECTED, {
                  value: selected.length,
                });
              }
              const labels = selected
                .map((val) => {
                  const found = options.find((opt) => opt.value === val);
                  return found
                    ? doTranslate
                      ? t(found.label)
                      : found.label
                    : val;
                })
                .slice(0, 4)
                .join(", ");
              return selected.length > 4 ? `${labels}, ...` : labels;
            }}
            MenuProps={MenuProps}
            {...field}
            value={Array.isArray(field.value) ? field.value : []}
            disabled={disabled}
            ref={field.ref}
            inputRef={field.ref}
            onChange={async (e) => {
              let value = e.target.value;
              // Handle select all
              if (showAllOption && value.includes("all")) {
                const newValue =
                  field.value.length === options.length ? [] : allOptionValues;
                field.onChange(newValue);
                actionsChange(newValue);
                return;
              }
              if (
                maxSelections != null &&
                Array.isArray(value) &&
                value.length > maxSelections
              ) {
                value = value.slice(0, maxSelections);
              }
              field.onChange(value);
              actionsChange(value);
            }}
            sx={{
              borderRadius: isRounded ? 4 : 1,
              backgroundColor,
              "& .MuiSelect-select": {
                "& .MuiChip-root": {
                  display: "none",
                },
                "& .tooltip-modal": {
                  display: "none",
                },
              },
            }}
          >
            {showEmptyOption && (
              <MenuItem disabled value="">
                <em>{t(SELECT_A_OPTION)}</em>
              </MenuItem>
            )}
            {showAddAction && (
              <Box sx={{ px: 2, py: 1 }} onKeyDown={(e) => e.stopPropagation()}>
                <TextField
                  label={labelAddAction}
                  error={!!errorAddAction}
                  helperText={errorAddAction}
                  variant={variant}
                  name="newOption"
                  hiddenLabel={hiddenLabel}
                  value={newOption}
                  onKeyDown={(e) => {
                    if (e.code === "Enter") {
                      const response = addAction(newOption);
                      if (response) setNewOption("");
                    }
                  }}
                  onChange={(e) => setNewOption(e.target.value)}
                  fullWidth
                />
              </Box>
            )}
            {showAllOption && (
              <MenuItem value="all">
                <Checkbox
                  checked={
                    field.value.length === options.length && options.length > 0
                  }
                  indeterminate={
                    field.value.length > 0 &&
                    field.value.length < options.length
                  }
                />
                <ListItemText primary={t(ANY)} />
              </MenuItem>
            )}
            {options.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{
                  display: option.isHidden ? "none" : "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: { xs: 1, sm: 2 },
                }}
              >
                <Checkbox checked={field.value.indexOf(option.value) > -1} />
                <ListItemText
                  primary={doTranslate ? t(option.label) : option.label}
                />
              </MenuItem>
            ))}
          </SelectMUI>
          {!!error && (
            <Typography variant="caption" color="error">
              {error?.message}
            </Typography>
          )}
          {!error && helperText && (
            <Typography variant="caption" color="textSecondary">
              {t(helperText)}
            </Typography>
          )}
        </FormControl>
      )}
    />
  );
};

export default SelectMultiple;
